import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaWritebackV1Service, serviceName } from '../service';
import { GetNoteStatusIO, ServiceQueries } from '../types';
import { useWritebackV1Queries } from './use-writeback-v1-queries';
import { UseWritebackV1QueryEndpointArgs, useWritebackV1Query } from './use-writeback-v1-query';

/**
 * A hook that returns a query for the `GetNoteStatus` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetNoteStatusQuery = <E = unknown, D = GetNoteStatusIO['output']>(
  args: UseWritebackV1QueryEndpointArgs<'GetNoteStatus', E, D>
) =>
  useWritebackV1Query({
    endpointName: 'GetNoteStatus',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetNoteStatus` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetNoteStatusQueries = <E = unknown, D = GetNoteStatusIO['output']>(
  argsArr: UseWritebackV1QueryEndpointArgs<'GetNoteStatus', E, D>[]
) =>
  useWritebackV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetNoteStatus',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetNoteStatus` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetNoteStatus = () => {
  const queryClient = useQueryClient();

  const fetchGetNoteStatus = async <E = unknown, D = GetNoteStatusIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseWritebackV1QueryEndpointArgs<'GetNoteStatus', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetNoteStatusIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetNoteStatus'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetNoteStatusIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetNoteStatus'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetNoteStatus'>({
          serviceName,
          endpointName: 'GetNoteStatus',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaWritebackV1Service.GetNoteStatus(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetNoteStatus;
};
